import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Alert, Box, Button, Grid, MenuItem, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import initialValues from '../../validation/lets-talk-liability/initialValues';
import validationSchema from '../../validation/lets-talk-liability/validationSchema';
import formModel from '../../validation/lets-talk-liability/formModel';
import InputField from './form-fields/InputField';
import WebsiteForms from './WebsiteForms';

const LetsTalkLiabilityFormRoot = styled('div')(({ theme }) => ({
    padding: '50px 0',
    // WebsiteForms sets the submit button margin inline, so it has to be overridden
    '& button[type="submit"]': {
        margin: '30px auto !important',
    },
}));

/**
 * Renders a "please specify" field only while its parent dropdown is set to "Other",
 * clearing the value again when the parent changes away from "Other".
 */
const SpecifyField = (props) => {
    const { dependsOn, name, label } = props;
    const { values, setFieldValue } = useFormikContext();
    const isVisible = values[dependsOn] === 'Other';

    useEffect(() => {
        if (!isVisible && values[name]) {
            setFieldValue(name, '');
        }
    }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!isVisible) return null;

    return (
        <Grid item xs={12} sm={6}>
            <InputField name={name} label={label} fullWidth />
        </Grid>
    );
};

const LetsTalkLiabilityForm = () => {
    const {
        firstName,
        lastName,
        email,
        contactNumber,
        companyName,
        role,
        roleOther,
        dietaryRequirements,
        dietaryOther,
    } = formModel;

    const [response, setResponse] = useState(undefined);

    if (response && response.isSuccessful) {
        return (
            <LetsTalkLiabilityFormRoot>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        padding: '0px 30px',
                    }}
                >
                    <Alert severity="success">You are registered!</Alert>

                    <Typography
                        variant="subtitle1"
                        maxWidth={1000}
                        textAlign="center"
                        marginTop={3}
                    >
                        Thank you for registering for Let's Talk Liability. We have sent a
                        confirmation to your email address, and we'll send a reminder closer to the
                        event together with a map and all the details you'll need to easily access
                        the venue.
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ marginTop: 4 }}
                        onClick={() => setResponse(undefined)}
                    >
                        Register Another Person
                    </Button>
                </Box>
            </LetsTalkLiabilityFormRoot>
        );
    }

    return (
        <LetsTalkLiabilityFormRoot>
            <WebsiteForms
                formUrl="api/forms/letstalkliability"
                initialValues={initialValues}
                validationSchema={validationSchema}
                btnText="Register"
                handleResponse={(result) => setResponse(result)}
            >
                <Grid container spacing={7}>
                    <Grid item xs={12} sm={6}>
                        <InputField name={firstName.name} label={firstName.label} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField name={lastName.name} label={lastName.label} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField name={email.name} label={email.label} type="email" fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField
                            name={contactNumber.name}
                            label={contactNumber.label}
                            type="tel"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <InputField name={companyName.name} label={companyName.label} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField name={role.name} label={role.label} select fullWidth>
                            {role.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </InputField>
                    </Grid>
                    <SpecifyField
                        dependsOn={role.name}
                        name={roleOther.name}
                        label={roleOther.label}
                    />
                    <Grid item xs={12} sm={6}>
                        <InputField
                            name={dietaryRequirements.name}
                            label={dietaryRequirements.label}
                            select
                            fullWidth
                        >
                            {dietaryRequirements.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </InputField>
                    </Grid>
                    <SpecifyField
                        dependsOn={dietaryRequirements.name}
                        name={dietaryOther.name}
                        label={dietaryOther.label}
                    />
                </Grid>
            </WebsiteForms>
        </LetsTalkLiabilityFormRoot>
    );
};

export default LetsTalkLiabilityForm;
